import {
  Box,
  CardContent,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  Stack,
  SxProps,
  TextField,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useEffect, useState } from "react";

import AppCard from "@/common/components/Card/AppCard";
import { ArrayHelper } from "@/common/helpers/array";
import { CustomFormikSubProps } from "@/common/helpers/formik";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useTenantProfileV2 } from "@/common/hooks/entity/tenant/useTenantProfileV2";
import { useMemoWithDeepCompare } from "@/common/hooks/memo/useMemoWithDeepCompare";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  BillToSourceType,
  EntityType,
  GeneralBillToInfoInputDto,
  GeneralStrictEntityRelationInputDto,
} from "@/core/api/generated/v1";

import AppIconButton from "../../../Button/AppIconButton";
import AppIcon from "../../../Icons/AppIcon";
import AppTypography from "../../../Text/AppTypography";
import DepartmentAutocomplete from "../../Department/DepartmentAutocomplete";
import LocationAutocomplete from "../../Location/LocationAutocomplete";
import GeneralStrictEntityRelationLink from "../Display/GeneralStrictEntityRelationLink";

export function prepareGeneralBillToInfoInputDtoForApi(
  dto: GeneralBillToInfoInputDto | null | undefined,
): GeneralBillToInfoInputDto | undefined {
  return dto?.name ? dto : undefined;
}

interface Props {
  formikProps: CustomFormikSubProps<GeneralBillToInfoInputDto | null | undefined>;
  /** Don't infer if undefined */
  inferFromEntities: GeneralStrictEntityRelationInputDto[] | undefined;
  sx?: SxProps<Theme>;
}

/** Usage flow:
 * - Auto-infer from the entities by default.
 * - User can change source type and select custom value.
 * - User can enter custom text.
 */
export function GeneralBillToInfoInput({ formikProps, inferFromEntities, sx }: Props) {
  const tenantProfile = useTenantProfileV2();

  const { values, errors, setValues } = formikProps;

  const [sourceTypeSelected, setSourceTypeSelected] = useState<BillToSourceType | undefined>(
    undefined,
  );

  const inferFromEntitiesComputed = useMemoWithDeepCompare(
    () => ArrayHelper.nullIfEmpty(inferFromEntities?.filter((x) => x.entityType && x.entityId)),
    [inferFromEntities],
  );

  // const canAutoInfer = values?.sourceType !== BillToSourceType.Custom;
  const canAutoInfer =
    !values?.isCustom || (sourceTypeSelected !== BillToSourceType.Custom && !values?.billToEntity);
  const isFallback = !values?.isCustom;

  const inferRequest = useApiRequest(
    apiClient.tenantRequestsApi.apiV1TenantToTenantTenantRequestsBillToInfoInferPost,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      tenantRequestInferBillToInfoInputDto: {
        fromEntities: inferFromEntitiesComputed || undefined,
        sourceType: sourceTypeSelected || values?.sourceType,
        isFallback: isFallback,
      },
    },
    {
      deps: [inferFromEntitiesComputed, canAutoInfer, sourceTypeSelected, isFallback],
      skip: ArrayHelper.isEmpty(inferFromEntitiesComputed) || !canAutoInfer,
    },
  );
  const inferred = inferRequest.data;
  const isLoading = inferRequest.isLoading;
  const isDisabled = isLoading;

  useEffect(() => {
    if (canAutoInfer && inferred) {
      setValues({
        ...values,
        ...inferred,
        isCustom: values?.isCustom || inferred.isCustom,
      });
    }
  }, [inferred]);

  return (
    <AppCard variant='outlined' variant2='data' sx={{ ...sx }}>
      <CardContent>
        <FormControl margin='none' fullWidth>
          <Stack gap={0.5}>
            <TextField
              sx={{ flex: 1 }}
              label='Bill to'
              disabled={isDisabled || values?.sourceType !== BillToSourceType.Custom}
              value={values?.name || ""}
              error={Boolean(errors?.name)}
              helperText={ValidationHelper.getFormikErrorsAsString(errors?.name)}
              slotProps={{
                input: {
                  startAdornment: isLoading && (
                    <InputAdornment position='start'>
                      <CircularProgress size={24} />
                    </InputAdornment>
                  ),
                  endAdornment: values?.name && (
                    <InputAdornment position='end'>
                      <AppIconButton
                        onClick={() => {
                          const newValues: typeof values = {
                            ...values,
                            name: undefined,
                          };
                          setValues(newValues);
                        }}
                      >
                        <AppIcon of='clear' />
                      </AppIconButton>
                    </InputAdornment>
                  ),
                },
              }}
              onChange={(e) => {
                const name = e.target.value;
                const newValues: typeof values = {
                  ...values,
                  name: name,
                };
                setValues(newValues);
              }}
            />

            <ToggleButtonGroup
              value={values?.sourceType}
              size='small'
              exclusive
              disabled={isDisabled}
              onChange={(e, newValue) => {
                const newValueTyped = newValue as BillToSourceType;
                const newValues: typeof values = {
                  isCustom: true,
                  name:
                    newValueTyped === BillToSourceType.Tenant
                      ? tenantProfile?.profile?.name
                      : newValueTyped === BillToSourceType.Custom
                        ? values?.name
                        : undefined,
                  sourceType: newValueTyped,
                  billToEntity:
                    newValueTyped === BillToSourceType.Tenant
                      ? {
                          entityType: EntityType.Company,
                          entityId: tenantProfile?.profile?.id,
                        }
                      : undefined,
                  inferredFromEntity: undefined,
                  inferredAffiliationInfo: undefined,
                };
                setValues(newValues);
                setSourceTypeSelected(newValueTyped);
              }}
            >
              <ToggleButton value={BillToSourceType.Tenant}>
                <AppIcon of='tenant' sx={{ mr: 0.5 }} /> Company
              </ToggleButton>
              <ToggleButton value={BillToSourceType.Department}>
                <AppIcon of='department' sx={{ mr: 0.5 }} /> Department
              </ToggleButton>
              <ToggleButton value={BillToSourceType.Location}>
                <AppIcon of='location' sx={{ mr: 0.5 }} /> Location
              </ToggleButton>
              <ToggleButton value={BillToSourceType.Custom}>
                <AppIcon of='edit' sx={{ mr: 0.5 }} /> Enter manually
              </ToggleButton>
            </ToggleButtonGroup>

            {values?.sourceType && (
              <Box>
                {values?.sourceType === BillToSourceType.Tenant && <></>}

                {values?.sourceType === BillToSourceType.Department && (
                  <FormControl margin='dense' fullWidth>
                    <DepartmentAutocomplete
                      entityId={values?.billToEntity?.entityId}
                      entity={undefined}
                      disabled={isDisabled}
                      size='small'
                      onChange={(newValue) => {
                        const newValues: typeof values = {
                          ...values,
                          isCustom: true,
                          name: newValue?.name,
                          billToEntity: newValue
                            ? {
                                entityType: EntityType.Department,
                                entityId: newValue.id,
                              }
                            : undefined,
                          inferredFromEntity: undefined,
                          inferredAffiliationInfo: undefined,
                        };
                        setValues(newValues);
                      }}
                    />
                  </FormControl>
                )}

                {values?.sourceType === BillToSourceType.Location && (
                  <FormControl margin='dense' fullWidth>
                    <LocationAutocomplete
                      entityId={values?.billToEntity?.entityId}
                      entity={undefined}
                      disabled={isDisabled}
                      size='small'
                      onChange={(newValue) => {
                        const newValues: typeof values = {
                          ...values,
                          isCustom: true,
                          name: newValue?.name,
                          billToEntity: newValue
                            ? {
                                entityType: EntityType.Location,
                                entityId: newValue.id,
                              }
                            : undefined,
                          inferredFromEntity: undefined,
                          inferredAffiliationInfo: undefined,
                        };
                        setValues(newValues);
                      }}
                    />
                  </FormControl>
                )}
              </Box>
            )}

            {!isLoading && !values?.isCustom && values?.inferredFromEntity && !errors?.name && (
              <AppTypography variant='body2'>
                {/* Using <GeneralStrictEntityRelationLink value={values?.billToEntity} />
              {values?.inferredFromEntity && (
                <>
                  {" "}
                  of <GeneralStrictEntityRelationLink value={values?.inferredFromEntity} />
                </>
              )} */}
                Auto-inferred from{" "}
                <GeneralStrictEntityRelationLink value={values?.inferredFromEntity} />
              </AppTypography>
            )}

            {!errors?.name && errors && (
              <FormHelperText error>
                {ValidationHelper.getFormikErrorsAsString(errors, { isIncludeNested: true })}
              </FormHelperText>
            )}
          </Stack>
        </FormControl>
      </CardContent>
    </AppCard>
  );
}
